import { useState, useRef, useCallback, useEffect } from 'react';
import { DialogueLine, SoulEntry, Clip } from '../types';
import { updateEntry } from '../services/entryService';

export interface UseStudioDialogueLinesProps {
  currentClip?: Clip | null;
  initialEntry?: SoulEntry | null;
  duration: number;
  activeEntry: SoulEntry | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  currentTime: number;
  applyVocalMuting?: (time: number, force?: boolean) => void;
}

export interface UseStudioDialogueLinesReturn {
  dialogueLines: DialogueLine[];
  setDialogueLines: React.Dispatch<React.SetStateAction<DialogueLine[]>>;
  dialogueLinesRef: React.MutableRefObject<DialogueLine[]>;
  selectedLineId: string | null;
  setSelectedLineId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedLineIds: Set<string>;
  setSelectedLineIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  inspectorFocus: 'user' | 'orig';
  setInspectorFocus: React.Dispatch<React.SetStateAction<'user' | 'orig'>>;
  selectedLine: DialogueLine | undefined;
  handleInspectLine: (lineId: string, layer?: 'user' | 'orig') => void;
  handleToggleLineSelection: (lineId: string, layer?: 'user' | 'orig') => void;
  handleSelectLine: (lineId: string, layer?: 'user' | 'orig') => void;
  handleSelectAllLines: () => void;
  handleDeselectAllLines: () => void;
  handlePrevLine: () => void;
  handleNextLine: () => void;
  handleUpdateLineCharacter: (lineId: string, characterName: string) => void;
  handleUpdateLineText: (lineId: string, text: string) => void;
  handleUpdateLineTiming: (lineId: string, newStart: number, newEnd: number) => void;
  handleNudgeTime: (lineId: string, type: 'start' | 'end', delta: number) => void;
  handleToggleLineMute: (lineId: string) => void;
  handleAddDialogueLine: () => void;
  handleDeleteDialogueLine: (lineId: string) => void;
  handleSplitLine: (lineId: string, splitPoint: number) => void;
  handleCombineWithNext: (lineId: string) => void;
}

export function useStudioDialogueLines({
  currentClip,
  initialEntry,
  duration,
  activeEntry,
  videoRef,
  currentTime,
  applyVocalMuting
}: UseStudioDialogueLinesProps): UseStudioDialogueLinesReturn {
  const [dialogueLines, setDialogueLines] = useState<DialogueLine[]>(() => {
    if (!currentClip || !currentClip.id) {
      return [];
    }
    if (initialEntry && initialEntry.clipId === currentClip.id && initialEntry.dialogueLines && initialEntry.dialogueLines.length > 0) {
      const dur = currentClip.duration || Math.max(5, currentClip.trimEnd - currentClip.trimStart);
      return initialEntry.dialogueLines.map((l, idx) => ({
        ...l,
        id: l.id || `line-${idx + 1}`,
        rawStart: l.rawStart !== undefined ? l.rawStart : (l.start ?? l.startTime ?? 0),
        rawEnd: l.rawEnd !== undefined ? l.rawEnd : (l.end ?? l.endTime ?? dur)
      }));
    }
    return [];
  });

  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);

  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(() => {
    // Scene open: all turned off so the user selects the dialogs they want to dub
    return new Set<string>();
  });

  const [inspectorFocus, setInspectorFocus] = useState<'user' | 'orig'>('orig');
  const dialogueLinesRef = useRef<DialogueLine[]>(dialogueLines);
  dialogueLinesRef.current = dialogueLines;

  // Synchronize and atomically reset dialogue lines whenever the active clip changes
  useEffect(() => {
    if (!currentClip || !currentClip.id) {
      setDialogueLines([]);
      dialogueLinesRef.current = [];
      setSelectedLineId(null);
      setSelectedLineIds(new Set());
      return;
    }

    if (initialEntry && initialEntry.clipId === currentClip.id && initialEntry.dialogueLines && initialEntry.dialogueLines.length > 0) {
      const dur = currentClip.duration || Math.max(5, currentClip.trimEnd - currentClip.trimStart);
      const enriched = initialEntry.dialogueLines.map((l, idx) => ({
        ...l,
        id: l.id || `line-${idx + 1}`,
        rawStart: l.rawStart !== undefined ? l.rawStart : (l.start ?? l.startTime ?? 0),
        rawEnd: l.rawEnd !== undefined ? l.rawEnd : (l.end ?? l.endTime ?? dur)
      }));
      setDialogueLines(enriched);
      dialogueLinesRef.current = enriched;
      setSelectedLineId(null);
      setSelectedLineIds(new Set());
    } else if (!activeEntry || activeEntry.clipId !== currentClip.id) {
      // Clean slate for newly captured or unlinked clips to prevent cross-clip leakage
      setDialogueLines([]);
      dialogueLinesRef.current = [];
      setSelectedLineId(null);
      setSelectedLineIds(new Set());
    }
  }, [currentClip?.id, initialEntry?.id]);

  // ──────────────────────────────────────────────
  // Timing & Muting Updates
  // ──────────────────────────────────────────────
  const handleUpdateLineTiming = useCallback((lineId: string, newStart: number, newEnd: number) => {
    const updated = dialogueLinesRef.current.map((line) => {
      if (line.id === lineId) {
        return {
          ...line,
          start: newStart,
          end: newEnd,
          startTime: newStart,
          endTime: newEnd,
          rawStart: line.rawStart !== undefined ? line.rawStart : (line.start ?? line.startTime ?? newStart),
          rawEnd: line.rawEnd !== undefined ? line.rawEnd : (line.end ?? line.endTime ?? newEnd)
        };
      }
      return line;
    });

    setDialogueLines(updated);
    dialogueLinesRef.current = updated;

    // Immediately update vocal muting so changes reflect without needing pause/seek
    if (applyVocalMuting) {
      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);
    }

    // Debounced auto-save to entry
    if (activeEntry) {
      updateEntry(activeEntry.id, { dialogueLines: updated }).catch((err) => {
        console.warn('Auto-save line timing note:', err);
      });
    }
  }, [applyVocalMuting, videoRef, currentTime, activeEntry]);

  const handleToggleLineMute = useCallback((lineId: string) => {
    const updated = dialogueLinesRef.current.map((line) =>
      line.id === lineId ? { ...line, isMutedForDubbing: !line.isMutedForDubbing } : line
    );
    setDialogueLines(updated);
    dialogueLinesRef.current = updated;
    if (activeEntry) {
      updateEntry(activeEntry.id, { dialogueLines: updated }).catch(() => {});
    }
  }, [activeEntry]);

  // ──────────────────────────────────────────────
  // Navigation
  // ──────────────────────────────────────────────
  const handlePrevLine = useCallback(() => {
    const lines = dialogueLinesRef.current;
    if (lines.length === 0) return;
    const curIdx = lines.findIndex((l) => l.id === selectedLineId);
    const nextIdx = curIdx <= 0 ? lines.length - 1 : curIdx - 1;
    setSelectedLineId(lines[nextIdx].id);
  }, [selectedLineId]);

  const handleNextLine = useCallback(() => {
    const lines = dialogueLinesRef.current;
    if (lines.length === 0) return;
    const curIdx = lines.findIndex((l) => l.id === selectedLineId);
    const nextIdx = curIdx >= lines.length - 1 ? 0 : curIdx + 1;
    setSelectedLineId(lines[nextIdx].id);
  }, [selectedLineId]);

  // ──────────────────────────────────────────────
  // Selection & Teleprompter Workflow Handlers
  // ──────────────────────────────────────────────
  // Inspect only (does NOT touch dubbing armed selection)
  const handleInspectLine = useCallback((lineId: string, layer: 'user' | 'orig' = 'orig') => {
    setSelectedLineId(lineId);
    setInspectorFocus(layer);
  }, []);

  // Dubbing arm/disarm toggle (used in teleprompter)
  const handleToggleLineSelection = useCallback((lineId: string, layer: 'user' | 'orig' = 'orig') => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
    setSelectedLineId(lineId);
    setInspectorFocus(layer);
  }, []);

  // Single line selection helper
  const handleSelectLine = useCallback((lineId: string, layer: 'user' | 'orig' = 'orig') => {
    setSelectedLineId(lineId);
    setSelectedLineIds(new Set([lineId]));
    setInspectorFocus(layer);
  }, []);

  const handleSelectAllLines = useCallback(() => {
    setSelectedLineIds(new Set(dialogueLinesRef.current.map((l) => l.id)));
  }, []);

  const handleDeselectAllLines = useCallback(() => {
    setSelectedLineIds(new Set());
  }, []);

  const handleUpdateLineCharacter = useCallback((lineId: string, characterName: string) => {
    setDialogueLines((prev) =>
      prev.map((l) => (l.id === lineId ? { ...l, characterName, speaker: characterName } : l))
    );
  }, []);

  const handleUpdateLineText = useCallback((lineId: string, text: string) => {
    setDialogueLines((prev) =>
      prev.map((l) => (l.id === lineId ? { ...l, text } : l))
    );
  }, []);

  const handleNudgeTime = useCallback((lineId: string, type: 'start' | 'end', delta: number) => {
    let updatedLines: DialogueLine[] = [];
    setDialogueLines((prev) => {
      const updated = prev.map((l) => {
        if (l.id !== lineId) return l;
        const curStart = l.start ?? l.startTime ?? 0;
        const curEnd = l.end ?? l.endTime ?? duration;
        const rawStart = l.rawStart !== undefined ? l.rawStart : curStart;
        const rawEnd = l.rawEnd !== undefined ? l.rawEnd : curEnd;
        if (type === 'start') {
          const newStart = Math.max(0, Math.min(curEnd - 0.2, Math.round((curStart + delta) * 10) / 10));
          return { ...l, start: newStart, startTime: newStart, rawStart, rawEnd };
        } else {
          const newEnd = Math.min(duration, Math.max(curStart + 0.2, Math.round((curEnd + delta) * 10) / 10));
          return { ...l, end: newEnd, endTime: newEnd, rawStart, rawEnd };
        }
      });
      updatedLines = updated;
      dialogueLinesRef.current = updated;
      return updated;
    });

    // Immediately update vocal muting so trim takes effect in real-time
    if (applyVocalMuting) {
      const cur = videoRef.current ? videoRef.current.currentTime : currentTime;
      applyVocalMuting(cur, true);
    }

    // Auto-save to entry
    if (activeEntry && updatedLines.length > 0) {
      updateEntry(activeEntry.id, { dialogueLines: updatedLines }).catch((err) => {
        console.warn('Auto-save line nudge note:', err);
      });
    }
  }, [duration, applyVocalMuting, videoRef, currentTime, activeEntry]);

  const handleAddDialogueLine = useCallback(() => {
    const lines = dialogueLinesRef.current;
    const count = lines.length;
    const lastLine = lines[count - 1];
    const newStart = lastLine ? Math.min(duration - 1, (lastLine.end ?? duration) + 0.4) : 0;
    const newEnd = Math.min(duration, newStart + 3.0);
    const newLine: DialogueLine = {
      id: `line-${Date.now()}`,
      speaker: `Character ${count + 1}`,
      characterName: `Character ${count + 1}`,
      start: newStart,
      end: newEnd,
      startTime: newStart,
      endTime: newEnd,
      rawStart: newStart,
      rawEnd: newEnd,
      text: `New sentence #${count + 1}`
    };
    setDialogueLines((prev) => [...prev, newLine]);
    setSelectedLineIds((prev) => new Set([...prev, newLine.id]));
    setSelectedLineId(newLine.id);
  }, [duration]);

  const handleDeleteDialogueLine = useCallback((lineId: string) => {
    setDialogueLines((prev) => prev.filter((l) => l.id !== lineId));
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      next.delete(lineId);
      return next;
    });
    setSelectedLineId((prev) => (prev === lineId ? null : prev));
  }, []);

  const handleSplitLine = useCallback((lineId: string, splitPoint: number) => {
    setDialogueLines((prev) => {
      const idx = prev.findIndex((l) => l.id === lineId);
      if (idx === -1) return prev;
      const line = prev[idx];
      const firstHalf: DialogueLine = {
        ...line,
        end: splitPoint,
        endTime: splitPoint,
        rawEnd: line.rawEnd !== undefined ? line.rawEnd : splitPoint
      };
      const secondHalf: DialogueLine = {
        ...line,
        id: `${lineId}-split-${Date.now()}`,
        start: splitPoint,
        startTime: splitPoint,
        rawStart: line.rawStart !== undefined ? line.rawStart : splitPoint,
        text: ''
      };
      const updated = [...prev];
      updated.splice(idx, 1, firstHalf, secondHalf);
      if (activeEntry) {
        updateEntry(activeEntry.id, { dialogueLines: updated }).catch(() => {});
      }
      return updated;
    });
  }, [activeEntry]);

  const handleCombineWithNext = useCallback((lineId: string) => {
    setDialogueLines((prev) => {
      const idx = prev.findIndex((l) => l.id === lineId);
      if (idx === -1 || idx >= prev.length - 1) return prev;
      const currentLine = prev[idx];
      const nextLine = prev[idx + 1];
      const combined: DialogueLine = {
        ...currentLine,
        end: nextLine.end,
        endTime: nextLine.endTime,
        rawEnd: nextLine.rawEnd !== undefined ? nextLine.rawEnd : nextLine.end,
        text: (currentLine.text || '') + ' ' + (nextLine.text || ''),
        originalText: (currentLine.originalText || '') + ' ' + (nextLine.originalText || '')
      };
      const updated = [...prev];
      updated.splice(idx, 2, combined);
      if (activeEntry) {
        updateEntry(activeEntry.id, { dialogueLines: updated }).catch(() => {});
      }
      return updated;
    });
  }, [activeEntry]);

  const selectedLine = dialogueLines.find((l) => l.id === selectedLineId);

  return {
    dialogueLines,
    setDialogueLines,
    dialogueLinesRef,
    selectedLineId,
    setSelectedLineId,
    selectedLineIds,
    setSelectedLineIds,
    inspectorFocus,
    setInspectorFocus,
    selectedLine,
    handleInspectLine,
    handleToggleLineSelection,
    handleSelectLine,
    handleSelectAllLines,
    handleDeselectAllLines,
    handlePrevLine,
    handleNextLine,
    handleUpdateLineCharacter,
    handleUpdateLineText,
    handleUpdateLineTiming,
    handleNudgeTime,
    handleToggleLineMute,
    handleAddDialogueLine,
    handleDeleteDialogueLine,
    handleSplitLine,
    handleCombineWithNext
  };
}
